import React, { useEffect, useMemo, useState } from "react";

// Phase 2.3.19 — refreshed first-visit tour. The retired Help Centre step
// has been removed; onboarding now ends cleanly after PREFS.
const TOUR_STEPS = [
  {
    title: "WELCOME TO SCARTLEAD.TV",
    body: "SCARTLEAD.TV works like a real television station: channels keep broadcasting whether you are watching them or not. This quick tour covers the controls you need to tune in.",
    selector: null,
    welcome: true,
  },
  {
    title: "POWER",
    body: "The red POWER button turns the CRT on and off. Long-press POWER to cycle the scanline effect.",
    selector: '[data-testid="power-button"]',
    allowInteraction: false,
  },
  {
    title: "REMOTE NAVIGATION",
    body: "The D-pad handles navigation. Outside the Guide, LEFT and RIGHT seek the current programme; inside the Guide, the D-pad moves around the live schedule.",
    selector: '[data-testid="dpad-cluster"]',
    allowInteraction: false,
  },
  {
    title: "SURF CHANNELS",
    body: "Use CH ▼▲ to move through channels. Tuning a channel joins whatever is broadcasting at that moment rather than restarting the programme.",
    selector: '[data-testid="channel-down"], [data-testid="channel-up"]',
    allowInteraction: true,
  },
  {
    title: "DIRECT TUNING + LAST",
    body: "Enter a channel number for direct tuning. LAST jumps back to the previous channel, and UPDATES shows newly added programmes and site changes.",
    selector: '[data-testid="digit-pad"]',
    allowInteraction: true,
  },
  {
    title: "THE CHANNEL GUIDE",
    body: "GUIDE opens the live programme schedule. Browse channels and real programme times without interrupting what is playing; WATCH NOW is the explicit tune action.",
    selector: '[data-testid="guide-button"]',
    allowInteraction: true,
  },
  {
    title: "SERIES INFORMATION",
    body: "While watching a TV series, press I to open Series Information. From there you can browse seasons and episodes, start the series from the beginning, or jump straight to an episode.",
    selector: null,
    allowInteraction: false,
  },
  {
    title: "VIEWER PREFS",
    body: "PREFS personalises your viewing experience, including cabinet sizing, display effects and other viewer options.",
    selector: '[data-testid="viewer-settings-toggle"]',
    allowInteraction: false,
  },
  {
    title: "READY TO TUNE IN",
    body: "That is everything you need to start watching. The first time you open the Guide, a short Guide tour will explain the live schedule in more detail.",
    selector: null,
    finish: true,
  },
];

const PAD = 8;

export default function FirstVisitTour({ open, onFinish, onSkip }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const step = TOUR_STEPS[index];
  const total = TOUR_STEPS.length;

  useEffect(() => {
    if (!open) {
      setIndex(0);
      setRect(null);
      return;
    }
    setIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const measure = () => {
      if (!step?.selector) {
        setRect(null);
        return;
      }
      const elements = Array.from(document.querySelectorAll(step.selector));
      if (!elements.length) {
        setRect(null);
        return;
      }

      const rects = elements.map((el) => el.getBoundingClientRect());
      const top = Math.min(...rects.map((r) => r.top));
      const left = Math.min(...rects.map((r) => r.left));
      const right = Math.max(...rects.map((r) => r.right));
      const bottom = Math.max(...rects.map((r) => r.bottom));
      setRect({
        top: Math.max(0, top - PAD),
        left: Math.max(0, left - PAD),
        right: Math.min(window.innerWidth, right + PAD),
        bottom: Math.min(window.innerHeight, bottom + PAD),
        width: Math.max(0, (right - left) + PAD * 2),
        height: Math.max(0, (bottom - top) + PAD * 2),
      });
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    const id = setInterval(measure, 300);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
  }, [open, index, step?.selector]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onSkip?.();
      } else if ((e.key === "ArrowRight" || e.key === "Enter") && !step?.finish) {
        e.preventDefault();
        setIndex((i) => Math.min(total - 1, i + 1));
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, step?.finish, total, onSkip]);

  const calloutStyle = useMemo(() => {
    if (!rect) return { left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: 340 };
    const width = 340;
    const margin = 18;
    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;
    let left;
    if (spaceRight >= width + margin) left = rect.right + margin;
    else if (spaceLeft >= width + margin) left = rect.left - width - margin;
    else left = Math.max(12, Math.min(window.innerWidth - width - 12, rect.left));
    const top = Math.max(12, Math.min(window.innerHeight - 230, rect.top));
    return { left, top, width };
  }, [rect]);

  if (!open) return null;

  const blockers = rect ? [
    { top: 0, left: 0, right: 0, height: rect.top },
    { top: rect.bottom, left: 0, right: 0, bottom: 0 },
    { top: rect.top, left: 0, width: rect.left, height: rect.height },
    { top: rect.top, left: rect.right, right: 0, height: rect.height },
  ] : [{ inset: 0 }];

  return (
    <div className="fixed inset-0 z-[95]" data-testid="first-visit-tour" style={{ fontFamily: "JetBrains Mono, monospace" }} aria-live="polite">
      {blockers.map((style, i) => <div key={i} className="fixed bg-black/75 backdrop-blur-[1px]" style={style} />)}
      {rect && (
        <>
          <div
            className="fixed pointer-events-none border-2 border-[var(--phosphor)] shadow-[0_0_22px_var(--phosphor)] rounded-lg"
            style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          />
          {!step.allowInteraction && (
            <div className="fixed" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
          )}
        </>
      )}

      <div
        className="fixed bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)] p-4 shadow-[0_0_24px_color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
        style={calloutStyle}
        data-testid="first-visit-tour-card"
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[10px] tracking-[0.24em] opacity-60">
            {step.welcome ? "QUICK START" : step.finish ? "COMPLETE" : `${index}/${total - 2}`}
          </div>
          {!step.finish && (
            <button type="button" onClick={() => onSkip?.()} className="text-[9px] tracking-[0.18em] opacity-60 hover:opacity-100" data-testid="tour-skip">
              SKIP TOUR
            </button>
          )}
        </div>

        <div className="text-[20px] tracking-[0.12em] mb-2" style={{ fontFamily: "VT323, monospace" }}>{step.title}</div>
        <div className="text-[11px] leading-relaxed text-neutral-200 mb-4">{step.body}</div>

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0 || step.finish}
            className="text-[10px] tracking-[0.18em] border border-neutral-700 px-3 py-1.5 disabled:opacity-30"
            data-testid="tour-back"
          >
            ◀ BACK
          </button>

          {step.finish ? (
            <button type="button" onClick={() => onFinish?.()} className="text-[10px] tracking-[0.18em] border border-[var(--phosphor)] px-3 py-1.5 hover:bg-[var(--phosphor)] hover:text-black" data-testid="tour-finish">
              START WATCHING ▶
            </button>
          ) : (
            <button type="button" onClick={() => setIndex((i) => Math.min(total - 1, i + 1))} className="text-[10px] tracking-[0.18em] border border-[var(--phosphor)] px-3 py-1.5 hover:bg-[var(--phosphor)] hover:text-black" data-testid={step.welcome ? "tour-start" : "tour-next"}>
              {step.welcome ? "START TOUR ▶" : "NEXT ▶"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
